import { useCallback } from "react";
import {
  Stack,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
} from "@mui/material";
import { upperCase } from "lodash/fp";
import { TripState } from "types/user";
import TripCommuteFrequency from "components/trips/TripCommuteFrequency";
import useNetworkTripCommuteFrequency from "hooks/trips/useNetworkTripCommuteFrequency";
import useDisplayMessage from "hooks/useDisplayMessage";
import { useMutation } from "@apollo/client";
import { DELETE_TRIP } from "graphql/queries";
import { removeTrip } from "redux/slices/shed/shedSlice";
import { useAppDispatch } from "redux/hooks";
import { CarState } from "types/cars";
import CarLogoBrand from "components/cars/CarLogoBrand";
import TripCardItems from "components/trips/TripCardItems";
import TripTravelModeIcon from "components/trips/TripTravelModeIcon";

type Props = {
  trip: TripState;
  tripCar: CarState;
};

const TripCard = ({ trip, tripCar }: Props) => {
  const { displayMessage } = useDisplayMessage();
  const [deleteTrip] = useMutation(DELETE_TRIP);

  const dispatch = useAppDispatch();

  const handleDeleteCar = useCallback(async () => {
    if (!trip.tripId) return;

    await deleteTrip({ variables: { tripId: trip.tripId } });

    dispatch(removeTrip(trip.tripId));
    displayMessage("Trip has been removed", "success");
  }, [trip.tripId, displayMessage, dispatch, deleteTrip]);

  const { commuteFrequency, increaseFrequency, decreaseFrequency } =
    useNetworkTripCommuteFrequency({
      trip,
    });

  return (
    <Card variant="outlined">
      <CardContent>
        <Box
          display="flex"
          pb={2}
          gap={0.5}
          alignItems="center"
          justifyContent="space-between"
        >
          <Chip
            label={`${upperCase(tripCar.carBrand)} ${upperCase(
              tripCar.carModel
            )}`}
          />
          <Box flexGrow={1} />
          <Button disabled={!tripCar} onClick={handleDeleteCar}>
            Delete
          </Button>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Stack gap={0.5} pb={2}>
            <TripCardItems
              localCommuteFrequency={commuteFrequency}
              trip={trip}
              tripCar={tripCar}
            />
          </Stack>
          <Stack>
            {trip.mapTravelMode === "DRIVING" ? (
              <CarLogoBrand size="full" carBrand={tripCar.carBrand} />
            ) : (
              <TripTravelModeIcon travelMode={trip.mapTravelMode} />
            )}

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {tripCar.licensePlate}
            </Typography>
          </Stack>
        </Box>
        <TripCommuteFrequency
          commuteFrequency={commuteFrequency}
          increaseFrequency={increaseFrequency}
          decreaseFrequency={decreaseFrequency}
        ></TripCommuteFrequency>
      </CardContent>
    </Card>
  );
};

export default TripCard;
